import React, { useState, useEffect } from "react";
import moment from "moment";
import {
  Typography,
  Input,
  message,
  Modal,
  Button,
  Form,
  Select,
  DatePicker,
  Upload,
  Icon,
  Row,
  Col,
  Divider,
} from "antd";
import { platformApi } from "../../../../api";
import { ENDPOINT } from "../../../../constants";

const { Title } = Typography;
const { Option } = Select;
const { Item } = Form;

let urls = ENDPOINT;

const InsuranceModal = ({
  form,
  open,
  close,
  vehicleId,
  bookingId,
  insurance,
}) => {
  const {
    getFieldDecorator,
    getFieldValue,
    validateFields,
    resetFields,
    setFieldsValue,
  } = form;

  const [insurances, setInsurances] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [required, setRequired] = useState(false);
  const [bulkUploads, setBulkUploads] = useState([]);
  const [savedBulkItems, setSavedBulkItems] = useState([]);

  useEffect(() => {
    platformApi.get("/api/insurance").then((result) => {
      let { data } = result;
      if (data.code === 200) {
        let { response } = data;
        if (response.code === 200) {
          setInsurances(response.data);
        }
      }
    });
  }, []);

  useEffect(() => {
    if (getFieldValue("insurer") && getFieldValue("insurer").length > 0) {
      setRequired(true);
    } else {
      setRequired(false);
    }
  }, [getFieldValue("insurer")]);

  useEffect(() => {
    const insuranceType = getFieldValue("insuranceType");
    const validFrom = getFieldValue("validFrom");

    if (!validFrom || !insuranceType) return;

    let yearsToAdd = null;

    if (insuranceType.includes("+5")) {
      yearsToAdd = 5;
    } else if (
      insuranceType.includes("+1") ||
      insuranceType === "1 Yr OD" ||
      insuranceType === "1 Yr OD Zero Dep"
    ) {
      yearsToAdd = 1;
    }

    if (yearsToAdd) {
      const newValidTo = moment(validFrom)
        .add(yearsToAdd, "years")
        .subtract(1, "days"); // e.g., 1 year minus a day
      setFieldsValue({ validTo: newValidTo });
    }
  }, [getFieldValue("insuranceType"), getFieldValue("validFrom")]);

  const searchInsurance = (searchString) => {
    platformApi
      .post("/api/options/get", {
        module: "insurances",
        column: "name",
        fields: ["name"],
        searchString: searchString,
        size: 100,
        searchColumns: ["name"],
      })
      .then((res) => {
        const { data } = res;
        if (data.code === 200) {
          setInsurances(data.response);
        }
      })
      .catch((err) => {
        console.error("Insurance Fetch Error: ", err);
        message.error("Couldn't Fetch Insurances");
      });
  };

  const handleBulkUpload = (file) => {
    const tempId = `${Date.now()}-${file.uid}`;
    const pendingItem = {
      tempId,
      file,
      insurer: undefined,
      policyNumber: "",
      insuranceType: "",
      validFrom: null,
      validTo: null,
      parsing: true,
    };

    setBulkUploads((prev) => [...prev, pendingItem]);
    parseInsuranceDoc(tempId, file);
    return false; // stop Upload from auto posting
  };

  const parseInsuranceDoc = async (tempId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      // backend should return parsed fields for the uploaded PDF
      const res = await platformApi.post("/api/insurance/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const parsed = res?.data?.response?.data || res?.data?.data || {};
      let insuranceType = parsed.insuranceType || "1+5";

      setBulkUploads((prev) =>
        prev.map((item) =>
          item.tempId === tempId
            ? {
              ...item,
              insurer: parsed.insurerId || item.insurer,
              policyNumber: parsed.policyNumber || item.policyNumber,
              insuranceType: insuranceType,
              validFrom: parsed.validFrom
                ? moment(parsed.validFrom, "DD/MM/YYYY")
                : item.validFrom,
              validTo: parsed.validTo
                ? moment(parsed.validTo, "DD/MM/YYYY")
                : item.validTo,
              parsing: false,
            }
            : item
        )
      );
    } catch (err) {
      console.error("Failed to parse insurance doc", err);
      message.error("Could not prefill from document");
      setBulkUploads((prev) =>
        prev.map((item) =>
          item.tempId === tempId ? { ...item, parsing: false } : item
        )
      );
    }
  };

  const updateBulkField = (tempId, key, value) => {
    setBulkUploads((prev) =>
      prev.map((item) =>
        item.tempId === tempId
          ? {
            ...item,
            [key]: value,
          }
          : item
      )
    );
  };

  const removeBulkItem = (tempId) => {
    setBulkUploads((prev) => prev.filter((item) => item.tempId !== tempId));
  };

  const saveBulkItem = (item) => {
    const {
      insurer,
      policyNumber,
      insuranceType,
      validFrom,
      validTo,
      file,
    } = item;

    if (item.parsing) {
      message.info("Parsing document... please wait");
      return;
    }

    if (
      !insurer ||
      !policyNumber ||
      !insuranceType ||
      !validFrom ||
      !validTo ||
      !file
    ) {
      message.warning("Fill all fields for this upload before saving");
      return;
    }

    // Check for duplicates in saved bulk items
    const duplicateInBulk = savedBulkItems.some(
      (row) => row.insurer === insurer || row.policyNumber === policyNumber
    );

    // Check for duplicates in form fields
    const formInsurer = getFieldValue("insurer");
    const formPolicyNumber = getFieldValue("policyNumber");
    const duplicateInForm =
      (formInsurer && formInsurer === insurer) ||
      (formPolicyNumber && formPolicyNumber === policyNumber);

    if (duplicateInBulk || duplicateInForm) {
      message.warning("Duplicate insurer or policy number");
      return;
    }

    const savedItem = {
      tempId: item.tempId,
      insurer,
      policyNumber,
      insuranceType,
      validFrom: moment(validFrom),
      validTo: moment(validTo),
      file,
    };

    setSavedBulkItems((prev) => [...prev, savedItem]);
    setBulkUploads((prev) => prev.filter((row) => row.tempId !== item.tempId));
    message.success("Insurance document saved");
  };

  const removeSavedBulkItem = (tempId) => {
    setSavedBulkItems((prev) => prev.filter((item) => item.tempId !== tempId));
    message.success("Insurance document removed");
  };

  const handleUpload = (info) => {
    if (info.file.status === "uploading") {
      setFileList([info.file]);
    }
    if (info.file.status === "done") {
      setFileList([info.file]);
    }
    if (info.file.status === "error") {
      message.error("File upload failed");
      setFileList([]);
    }
  };

  const handleDelete = () => {
    setFileList([]);
    setFileUrl(null);
    setFieldsValue({ pdf: undefined });
  };

  const getBase64 = (pdf, callback) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => callback(reader.result));
    reader.readAsDataURL(pdf);
  };

  const showPDF = (file) => {
    if (file.url) {
      window.open(file.url, "_blank");
    } else if (file.originFileObj) {
      getBase64(file.originFileObj, (url) => {
        let pdfWindow = window.open("");
        pdfWindow.document.write(
          "<title>" +
          file.name +
          '</title><iframe type="application/pdf"  frameBorder="0"  width="100%" height="100%" title="' +
          file.name +
          '" src="' +
          url +
          '"></iframe>'
        );
      });
    }
  };

  const handleSubmit = () => {
    // Check if there are any unsaved bulk items
    if (bulkUploads.length > 0) {
      message.warning(
        "Please save or remove all bulk upload items before submitting"
      );
      return;
    }

    // Collect all insurance items (bulk + single form)
    const allInsuranceItems = [];

    // Add saved bulk items
    if (savedBulkItems.length > 0) {
      savedBulkItems.forEach((item) => {
        allInsuranceItems.push({
          insurer: item.insurer,
          policyNumber: item.policyNumber,
          insuranceType: item.insuranceType,
          validFrom: item.validFrom.toISOString(),
          validTo: item.validTo.toISOString(),
          file: item.file,
        });
      });
    }

    // Check if single form has data
    const hasFormData =
      getFieldValue("insurer") && getFieldValue("policyNumber");

    if (hasFormData) {
      validateFields(async (err) => {
        if (!err) {
          const pdfField = getFieldValue("pdf");
          const file =
            fileList.length > 0
              ? fileList[0]
              : pdfField && pdfField.fileList && pdfField.fileList.length > 0
                ? pdfField.fileList[0]
                : null;

          if (!file || !file.originFileObj) {
            message.error("Please upload a document");
            return;
          }

          // Add single form item
          allInsuranceItems.push({
            insurer: getFieldValue("insurer"),
            policyNumber: getFieldValue("policyNumber"),
            insuranceType: getFieldValue("insuranceType"),
            validFrom: moment(getFieldValue("validFrom")._d).toISOString(),
            validTo: moment(getFieldValue("validTo")._d).toISOString(),
            file: file.originFileObj,
          });

          await submitInsurance(allInsuranceItems);
        }
      });
    } else if (allInsuranceItems.length > 0) {
      // Submit only bulk items
      submitInsurance(allInsuranceItems);
    } else {
      message.error("Please add at least one insurance document");
    }
  };

  const submitInsurance = async (insuranceItems) => {
    if (insuranceItems.length === 0) {
      message.error("Please add at least one insurance document");
      return;
    }

    setUploading(true);

    try {
      const updateFormData = new FormData();
      updateFormData.append("module", "Vehicle");
      updateFormData.append("master", "Transaction Master");
      updateFormData.append("type", "Insurance-Update");
      updateFormData.append("id", vehicleId);

      const insurancePayload = insuranceItems.map((item, index) => {
        updateFormData.append(`${index}`, item.file, item.file.name);
        return {
          id: "",
          insurer: item.insurer,
          policyNumber: item.policyNumber,
          insuranceType: item.insuranceType,
          validFrom: item.validFrom,
          validTo: item.validTo,
          pdf: {
            event: { isTrusted: true, percent: 100 },
            file: {
              name: item.file.name,
              type: item.file.type,
            },
            fileList: [
              {
                name: item.file.name,
                status: "done",
                response: { code: 200, msg: "mocky" },
              },
            ],
          },
          url: "",
        };
      });

      updateFormData.append(
        "vehicleData",
        JSON.stringify({ insuranceData: insurancePayload })
      );

      const res = await platformApi.put(
        `/api/vehicle/${vehicleId}`,
        updateFormData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      const { data } = res;
      if (data.code === 200) {
        message.success("Insurance updated successfully");

        if (bookingId) {
          try {
            const statusRes = await platformApi.put(
              `/api/saleRegister/updateStatus/${bookingId}`,
              { status: "INSURANCE" }
            );

            if (statusRes.data.code === 200) {
              message.success("Booking status updated to INSURANCE");
            } else {
              message.warning(
                "Vehicle updated, but booking status update failed"
              );
            }
          } catch (err) {
            console.error("Error updating booking status:", err);
            message.warning(
              "Vehicle updated, but booking status update failed"
            );
          }
        }

        resetFields();
        setFileList([]);
        setFileUrl(null);
        setBulkUploads([]);
        setSavedBulkItems([]);
        close();
      } else {
        throw new Error("Unexpected server response");
      }
    } catch (error) {
      console.error("Error adding insurance:", error);
      message.error("Failed to add insurance");
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    resetFields();
    setFileList([]);
    setFileUrl(null);
    setBulkUploads([]);
    setSavedBulkItems([]);
    close();
  };

  return (
    <Modal
      title={<Title level={4}>Add Insurance Documents</Title>}
      visible={open}
      onCancel={handleCancel}
      width={900}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="ok"
          type="primary"
          onClick={handleSubmit}
          loading={uploading}
        >
          Save
        </Button>,
      ]}
    >
      <Form>
        <h3>Upload Insurance</h3>
        <Upload
          beforeUpload={handleBulkUpload}
          fileList={[]}
          multiple
          accept=".pdf"
          showUploadList={false}
        >
          <Button type="primary" ghost>
            <Icon type="upload" /> Upload Insurance
          </Button>
        </Upload>
        {bulkUploads.length > 0 ? (
          <>
            <Divider />
            {bulkUploads.map((item) => (
              <div
                key={item.tempId}
                style={{
                  border: "1px solid #f0f0f0",
                  padding: "12px",
                  marginBottom: "12px",
                  marginTop: "12px",
                  borderRadius: "6px",
                }}
              >
                <Row gutter={16}>
                  <Col span={8}>
                    <Item label="Insurer">
                      <Select
                        placeholder="Insurer"
                        value={item.insurer}
                        showSearch
                        allowClear
                        filterOption={(input, option) => {
                          const label =
                            typeof option?.props?.children === "string"
                              ? option.props.children
                              : option?.props?.children?.toString?.() || "";
                          return label
                            .toLowerCase()
                            .includes(input.toLowerCase());
                        }}
                        onSearch={(e) => {
                          if (e) {
                            searchInsurance(e);
                          } else {
                            platformApi.get("/api/insurance").then((result) => {
                              let { data } = result;
                              if (data.code === 200) {
                                let { response } = data;
                                if (response.code === 200) {
                                  setInsurances(response.data);
                                }
                              }
                            });
                          }
                        }}
                        onChange={(val) =>
                          updateBulkField(item.tempId, "insurer", val)
                        }
                      >
                        {insurances.map((ins) => (
                          <Option value={ins.id} key={ins.id}>
                            {ins.name}
                          </Option>
                        ))}
                      </Select>
                    </Item>
                  </Col>
                  <Col span={8}>
                    <Item label="Policy No">
                      <Input
                        placeholder="Policy No"
                        value={item.policyNumber}
                        onChange={(e) =>
                          updateBulkField(
                            item.tempId,
                            "policyNumber",
                            e.target.value
                          )
                        }
                      />
                    </Item>
                  </Col>
                  <Col span={8}>
                    <Item label="Insurance Type">
                      <Select
                        placeholder="Insurance Type"
                        value={item.insuranceType}
                        onChange={(val) =>
                          updateBulkField(item.tempId, "insuranceType", val)
                        }
                        allowClear
                      >
                        <Option value="1+5">1+5</Option>
                        <Option value="5+5">5+5</Option>
                        <Option value="1+5 Zero Dep">1+5 Zero Dep</Option>
                        <Option value="5+5 Zero Dep">5+5 Zero Dep</Option>
                        <Option value="1+1">1+1</Option>
                        <Option value="1+1 Zero Dep">1+1 Zero Dep</Option>
                        <Option value="1 Yr OD">1 Yr OD</Option>
                        <Option value="1 Yr OD Zero Dep">
                          1 Yr OD Zero Dep
                        </Option>
                        <Option value="3rd Party">3rd Party</Option>
                      </Select>
                    </Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={8}>
                    <Item label="Valid From">
                      <DatePicker
                        format="DD-MM-YYYY"
                        style={{ width: "100%" }}
                        value={item.validFrom ? moment(item.validFrom) : null}
                        onChange={(val) =>
                          updateBulkField(item.tempId, "validFrom", val)
                        }
                        placeholder="Valid From"
                      />
                    </Item>
                  </Col>
                  <Col span={8}>
                    <Item label="Valid To">
                      <DatePicker
                        format="DD-MM-YYYY"
                        style={{ width: "100%" }}
                        value={item.validTo ? moment(item.validTo) : null}
                        disabledDate={(m) =>
                          item.validFrom ? m._d <= item.validFrom._d : false
                        }
                        onChange={(val) =>
                          updateBulkField(item.tempId, "validTo", val)
                        }
                        placeholder="Valid To"
                      />
                    </Item>
                  </Col>
                  <Col
                    span={8}
                    style={{ display: "flex", alignItems: "flex-end" }}
                  >
                    <div style={{ width: "100%" }}>
                      <div style={{ marginBottom: "8px" }}>
                        File: <strong>{item.file?.name}</strong>
                      </div>
                      {item.parsing ? (
                        <div style={{ color: "#999", marginBottom: 8 }}>
                          Prefilling from document...
                        </div>
                      ) : null}
                      <Button
                        type="primary"
                        onClick={() => saveBulkItem(item)}
                        disabled={item.parsing}
                        style={{ marginRight: 8 }}
                      >
                        Save
                      </Button>
                      <Button onClick={() => removeBulkItem(item.tempId)}>
                        Remove
                      </Button>
                    </div>
                  </Col>
                </Row>
              </div>
            ))}
          </>
        ) : null}
        {savedBulkItems.length > 0 && (
          <>
            <Divider />
            <div style={{ marginBottom: "16px" }}>
              <strong>
                Saved Insurance Documents ({savedBulkItems.length}):
              </strong>
              <ul style={{ marginTop: "8px", listStyle: "none", padding: 0 }}>
                {savedBulkItems.map((item) => (
                  <li
                    key={item.tempId}
                    style={{
                      marginBottom: "8px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span>
                      {item.file.name} - Policy: {item.policyNumber}
                    </span>
                    <Button
                      type="link"
                      danger
                      size="small"
                      onClick={() => removeSavedBulkItem(item.tempId)}
                    >
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </Form>
    </Modal>
  );
};

const WrappedInsuranceModal = Form.create()(InsuranceModal);

export default WrappedInsuranceModal;
